import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const InfoBox = styled.div`
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const InfoIcon = styled.span`
  font-size: 20px;
`;

const InfoText = styled.div`
  font-size: 13px;
  color: #1e40af;
  line-height: 1.5;
`;

const DongengGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const DongengCard = styled.div<{ $inactive?: boolean }>`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid ${p => p.$inactive ? '#fecaca' : '#e2e8f0'};
  opacity: ${p => p.$inactive ? 0.7 : 1};
  transition: all 0.2s;
`;

const Thumbnail = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: #f1f5f9;
  overflow: hidden;
`;

const ThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const InactiveBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(239, 68, 68, 0.9);
  color: #fff;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
`;

const CardContent = styled.div`
  padding: 16px;
`;

const VideoTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
  margin: 0 0 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const VideoMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 12px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
`;

const ToggleSwitch = styled.button<{ $active: boolean }>`
  width: 48px;
  height: 26px;
  border-radius: 13px;
  border: none;
  background: ${p => p.$active ? '#2DBCAF' : '#e2e8f0'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${p => p.$active ? '25px' : '3px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const StatusText = styled.span<{ $active: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$active ? '#16a34a' : '#dc2626'};
`;

const ViewBtn = styled.a`
  padding: 6px 12px;
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background: rgba(59, 130, 246, 0.15);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const PageInfo = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const PageBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  padding: 8px 14px;
  border: 1px solid ${p => p.$active ? '#2DBCAF' : '#e2e8f0'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  background: ${p => p.$active ? '#2DBCAF' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#64748b'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) { 
    border-color: #2DBCAF;
    color: ${p => p.$active ? '#fff' : '#2DBCAF'};
  }
  &:disabled { 
    opacity: 0.5; 
    cursor: not-allowed; 
  }
`;

const ITEMS_PER_PAGE = 12;

interface Dongeng {
  videoId: string;
  title: string;
  thumbnail: string;
  published: string;
  isActive: boolean;
}

const DongengPage: React.FC = () => {
  const [dongeng, setDongeng] = useState<Dongeng[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchDongeng = async () => {
      try {
        const res = await fetch('/api/admin/dongeng', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setDongeng(data.dongeng || []);
        }
      } catch (err) {
        console.error('Failed to fetch dongeng:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDongeng();
  }, []);

  // Pagination calculations
  const totalPages = Math.ceil(dongeng.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const paginatedDongeng = dongeng.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleToggle = async (videoId: string, currentActive: boolean, title: string, thumbnail: string) => {
    try {
      const res = await fetch(`/api/admin/dongeng/${videoId}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !currentActive, title, thumbnail }),
      });
      
      if (res.ok) {
        setDongeng(dongeng.map(d => 
          d.videoId === videoId ? { ...d, isActive: !currentActive } : d
        ));
      }
    } catch (err) {
      console.error('Failed to toggle dongeng:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return <LoadingState>Loading dongeng...</LoadingState>;
  }

  return (
    <>
      <PageHeader>
        <Title>Dongeng</Title>
        <Subtitle>Kelola video dongeng dari YouTube</Subtitle>
      </PageHeader>

      <InfoBox>
        <InfoIcon>💡</InfoIcon>
        <InfoText>
          Video dongeng diambil dari channel YouTube Kindergame. 
          Toggle status untuk menyembunyikan atau menampilkan video di halaman pengguna.
        </InfoText>
      </InfoBox>

      {dongeng.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📖</div>
          <div>Tidak ada video dongeng ditemukan</div>
        </EmptyState>
      ) : (
        <>
          <DongengGrid>
            {paginatedDongeng.map((item) => (
              <DongengCard key={item.videoId} $inactive={!item.isActive}>
                <Thumbnail>
                  <ThumbnailImg src={item.thumbnail} alt={item.title} />
                  {!item.isActive && <InactiveBadge>NONAKTIF</InactiveBadge>}
                </Thumbnail>
                <CardContent>
                  <VideoTitle>{item.title}</VideoTitle>
                  <VideoMeta>
                    {formatDate(item.published)}
                  </VideoMeta>
                  <CardActions>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ToggleSwitch
                        $active={item.isActive}
                        onClick={() => handleToggle(item.videoId, item.isActive, item.title, item.thumbnail)}
                        title={item.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                      />
                      <StatusText $active={item.isActive}>
                        {item.isActive ? 'Aktif' : 'Nonaktif'}
                      </StatusText>
                    </div>
                    <ViewBtn 
                      href={`https://youtube.com/watch?v=${item.videoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ▶ Lihat
                    </ViewBtn>
                  </CardActions>
                </CardContent>
              </DongengCard>
            ))}
          </DongengGrid>

          {totalPages > 1 && (
            <Pagination>
              <PageInfo>
                Menampilkan {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, dongeng.length)} dari {dongeng.length} video
              </PageInfo>
              <PageBtns>
                <PageBtn
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  ← Prev
                </PageBtn>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <PageBtn
                      key={pageNum}
                      $active={page === pageNum}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PageBtn>
                  );
                })}
                <PageBtn
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next →
                </PageBtn>
              </PageBtns>
            </Pagination>
          )}
        </>
      )}
    </>
  );
};

export default DongengPage;
